import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { userActions } from 'state/modules/user';
import { UserGroupsSelect } from 'components/AsyncFields';
import { TextField } from 'components/FormFields';

const CreateUserModal = ({ open, handleClick }) => {
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const CreateUserSchema = Yup.object().shape({
    user_group_id: Yup.number().required(i18n.t('common.required_field')),
    email: Yup.string()
      .email(i18n.t('common.invalid_email'))
      .required(i18n.t('common.required_field')),
    first_name: Yup.string().required(i18n.t('common.required_field')),
    last_name: Yup.string().required(i18n.t('common.required_field')),
    mobile_phone: Yup.string(),
    is_customer: Yup.boolean(),
  });

  const { errors, fetchingCreateUser, fetchedCreateUserSuccess } = useSelector(
    (state) => state.user
  );

  useEffect(() => {
    dispatch(userActions.resetCreateUser());
  }, [dispatch]);

  useEffect(() => {
    if (fetchedCreateUserSuccess) {
      handleClick(true);
      dispatch(userActions.resetCreateUser());
    }
  }, [fetchedCreateUserSuccess, handleClick, dispatch]);

  /**
   * Function to be executed after form validation
   * @param {object} values Payload to create user
   */
  const onSubmit = (values) => {
    const { mobile_phone, ...valuesNoMobile } = values;
    if (mobile_phone) {
      dispatch(userActions.createUser(values));
    } else {
      dispatch(userActions.createUser(valuesNoMobile));
    }
  };

  /**
   * Handle form submit
   */
  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onCancel={handleClick}
      onEnter={handleSubmit}
      enableCloseButton={false}
      loading={fetchingCreateUser}
      title={i18n.t('users.create')}
      enterButtonLabel={i18n.t('users.create')}
      enterButtonProps={{ 'data-cy': 'create-user' }}
      privileges={{ action: 'create', subject: 'user' }}
    >
      <Formik
        innerRef={formRef}
        initialValues={{
          email: '',
          last_name: '',
          first_name: '',
          mobile_phone: '',
          user_group_id: '',
          is_customer: false,
        }}
        onSubmit={onSubmit}
        validationSchema={CreateUserSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Box mt={1}>
              <Field
                margin="none"
                name="user_group_id"
                component={UserGroupsSelect}
                label={i18n.t('users.user_group')}
                customParams={{ user_group_type: ['DRIVER'], status_filter: 'exclude' }}
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="first_name"
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.first_name}
                label={i18n.t('fields.first_name')}
                data-cy="first_name-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="last_name"
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.last_name}
                label={i18n.t('fields.last_name')}
                data-cy="last_name-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                type="email"
                name="email"
                margin="none"
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.email}
                label={i18n.t('fields.email')}
                data-cy="email-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                margin="none"
                label="Phone"
                required={false}
                name="mobile_phone"
                component={TextField}
                asyncErrors={errors?.mobile_phone}
                data-cy="mobile_phone-input"
              />
            </Box>
            <Box mt={1}>
              <Field
                type="text"
                margin="none"
                name="internal_id"
                required={false}
                inputProperties={{}}
                component={TextField}
                asyncErrors={errors?.internal_id}
                label={i18n.t('customers.internal_id')}
                data-cy="internal_id-input"
              />
            </Box>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

CreateUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

CreateUserModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default CreateUserModal;
